import { useState } from 'react';
import { format } from 'date-fns';
import { FileText, Eye, ExternalLink } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  type LegalDocumentType,
  DOCUMENT_TYPE_CONFIG,
  useLegalDocumentVersions,
} from '@/hooks/useLegalDocuments';
import { LegalDocumentPreviewModal } from './LegalDocumentPreviewModal';

interface ActiveDocumentCardProps {
  documentType: LegalDocumentType;
}

const PUBLIC_ROUTES: Record<LegalDocumentType, string | null> = {
  'terms-of-service': '/terms',
  'privacy-policy': '/privacy',
  'po-terms': '/po-terms',
  'po-terms-summary': null, // Not yet published as a public page
};

export function ActiveDocumentCard({ documentType }: ActiveDocumentCardProps) {
  const config = DOCUMENT_TYPE_CONFIG[documentType];
  const { data: versions, isLoading } = useLegalDocumentVersions(documentType);
  const [showPreview, setShowPreview] = useState(false);

  const activeDocument = versions?.find((doc) => doc.is_active);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Current Active Version
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-24 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (!activeDocument) {
    return (
      <Card className="border-dashed">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Current Active Version
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-6">
            <p className="text-muted-foreground mb-2">No active version</p>
            <p className="text-sm text-muted-foreground">
              Upload and activate a document to display it on the public {config.label} page.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Current Active Version
            </CardTitle>
            <Badge className="bg-green-500/10 text-green-600 border-green-500/20">Active</Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <h3 className="font-semibold text-lg">{activeDocument.title}</h3>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground mt-1">
                <span>Version: {activeDocument.version}</span>
                {activeDocument.effective_date && (
                  <span>
                    Effective: {format(new Date(activeDocument.effective_date + 'T12:00:00'), 'MMMM d, yyyy')}
                  </span>
                )}
                {activeDocument.activated_at && (
                  <span>
                    Activated: {format(new Date(activeDocument.activated_at), 'MMMM d, yyyy \'at\' h:mm a')}
                  </span>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setShowPreview(true)}>
                <Eye className="h-4 w-4 mr-2" />
                Preview
              </Button>
              {PUBLIC_ROUTES[documentType] && (
                <Button variant="outline" size="sm" asChild>
                  <a href={PUBLIC_ROUTES[documentType]!} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="h-4 w-4 mr-2" />
                    View Public Page
                  </a>
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <LegalDocumentPreviewModal
        document={showPreview ? activeDocument : null}
        onClose={() => setShowPreview(false)}
      />
    </>
  );
}
